/* eslint-disable jsdoc/require-jsdoc, jsdoc/require-returns, jsdoc/require-param */
import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import axiosInstance from '../_helpers/interceptor';
import { DEV_NEW_DOMAIN_URL } from '../environment';
import { getUserNameFromSessionStorage } from '../services/session-storage-service';
import { openBleStream, StreamStatus } from '../services/ble-live-stream';
import LiveLineChart, { LivePoint, LiveSeries } from '../components/BleLive/LiveLineChart';
import LiveTrackMap, {
  ThresholdBand,
  Track,
  TrackColoring
} from '../components/BleLive/LiveTrackMap';
import ChannelHeatMap from '../components/BleLive/ChannelHeatMap';
import {
  BleChannelsResponse,
  downloadBlePingCsv,
  getBleChannels
} from '../services/ble-admin.service';
import { formatBytes } from '../services/streaming-download';
import { Filters, getAppliedFilter, setAppliedFilter } from '../services/localstorage.service';
import ParameterBoxPlot, {
  computeStats,
  fmt,
  ParameterStats
} from '../components/BleLive/ParameterBoxPlot';
import { CARD_CLASS } from '../components/ParticleBins/particleBinShared';
import { canUseBleAdmin } from '../_helpers/featureAccess';
import { useData } from '../context/data.context';
import { getActiveConfig, getPriorityColor, getThresholds } from '../_helpers/dashboardHelper';
import { COLOR_BY_PRIORITY } from '../constants/global-constants';
import { DeviceType, PriorityList } from '../constants/telosair-interfaces';
import { bleLabelOf, bleParameterOf, bleUnitOf } from '../_helpers/bleParameters';
import {
  EACH_TARGETS,
  EachRow,
  KeyedSamples,
  computeEachRow,
  roamRadiusMetres
} from '../_helpers/bleEach';
import VentilationCard from '../components/BleLive/VentilationCard';

const BASE = `${DEV_NEW_DOMAIN_URL}/ble-pings/admin`;

/** Most rows kept in memory, whatever the window — bounds a tab left open all day. */
const MAX_ROWS = 20000;
/** Rows per backfill request — the admin listing's own maximum. */
const HISTORY_LIMIT = 500;
/**
 * Backfill pages to fetch at most, so re-entering the page costs a bounded
 * number of requests.
 *
 * WHY MORE THAN ONE: the backfill used to be a single 500-row call, and a 1 Hz
 * phone fills 500 rows in about eight minutes — so coming back to a 15-minute
 * window showed only the newest eight minutes of it and looked like the
 * history had been lost. Six pages covers a full hour at 1 Hz, which is the
 * largest window this page offers.
 */
const MAX_HISTORY_PAGES = 6;
/** Array parameter drawn as the channel heat map. */
const CHANNEL_KEY = 'channel_data';
/** Redraw at most this often, however fast pings arrive. */
const RENDER_INTERVAL_MS = 250;

/**
 * Live window choices.
 *
 * The LARGEST of these governs how much is held in memory (see `merge`) and
 * how far the backfill pages back, so adding a longer window costs buffer
 * rather than extra requests. One hour at 1 Hz is 3,600 rows, well inside
 * MAX_ROWS.
 */
const WINDOWS = [
  { key: 1, label: '1 min' },
  { key: 5, label: '5 min' },
  { key: 15, label: '15 min' },
  { key: 30, label: '30 min' },
  { key: 60, label: '1 hour' }
];

/** "Go back in time" ranges, ending now. */
const HISTORY_PRESETS = [
  { key: '15m', label: '15 min', ms: 900000 },
  { key: '1h', label: '1 hour', ms: 3600000 },
  { key: '6h', label: '6 hours', ms: 6 * 3600000 },
  { key: '24h', label: '24 hours', ms: 24 * 3600000 },
  { key: '7d', label: '7 days', ms: 7 * 24 * 3600000 },
  { key: '30d', label: '30 days', ms: 30 * 24 * 3600000 }
];
/** The server rejects longer ranges. */
const MAX_HISTORY_MS = 31 * 24 * 3600000;
/** Fitting windows offered for the eACH calculation, in minutes. */
const EACH_WINDOWS = [5, 15, 30, 60];
/**
 * How often the eACH fit is redone in live mode.
 *
 * The fit is a 1-D search over a few hundred binned points per parameter —
 * cheap, but not cheap enough to redo on every stream tick four times a
 * second for a number that moves slowly.
 */
const EACH_INTERVAL_MS = 5000;

const PALETTE = [
  '#2E90FA',
  '#F79009',
  '#12B76A',
  '#EE46BC',
  '#7A5AF8',
  '#F04438',
  '#06AED4',
  '#EAAA08'
];
const TRACK_COLOR = '#2E90FA';

type PingValue = number | string | boolean | null;

type BlePing = {
  id: number;
  deviceSerial: number;
  deviceName: string | null;
  observedAt: number;
  values: Record<string, PingValue> | null;
  latitude: number | null;
  longitude: number | null;
  altitude: number | null;
  accelX: number | null;
  accelY: number | null;
  accelZ: number | null;
};

type BleDevice = {
  deviceSerial: number;
  deviceName: string | null;
  lastSeenAt: number;
  pingCount: number;
};

type HistoryPoint = {
  t: number;
  n: number;
  latitude: number | null;
  longitude: number | null;
  altitude: number | null;
  accelX: number | null;
  accelY: number | null;
  accelZ: number | null;
  accelPeak: number | null;
  values: Record<string, number>;
};

type HistoryStats = Omit<ParameterStats, 'latest'>;

type HistoryData = {
  deviceSerial: number;
  from: number;
  to: number;
  bucketMs: number;
  rawCount: number;
  points: HistoryPoint[];
  stats: Record<string, HistoryStats>;
};

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

function statusPill(status: StreamStatus): { label: string; className: string } {
  switch (status) {
    case 'live':
      return {
        label: 'Live',
        className: 'border-success-500/50 bg-success-500/15 text-success-400'
      };
    case 'connecting':
      return { label: 'Connecting…', className: 'border-gray-600 bg-gray-500/15 text-gray-300' };
    case 'reconnecting':
      return {
        label: 'Reconnecting…',
        className: 'border-warning-500/50 bg-warning-500/15 text-warning-300'
      };
    case 'denied':
      return {
        label: 'Not authorised',
        className: 'border-error-500/50 bg-error-500/15 text-error-400'
      };
    default:
      return { label: 'Offline', className: 'border-gray-600 bg-gray-500/15 text-gray-400' };
  }
}

function ageLabel(ms: number): string {
  if (ms < 2000) return 'just now';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  return m < 60 ? `${m}m ago` : `${Math.round(m / 60)}h ago`;
}

/** "3 s", "2 min", "1 h" — the width of one averaged history point. */
function bucketLabel(ms: number): string {
  if (ms < 60000) return `${Math.round(ms / 1000)} s`;
  if (ms < 3600000) return `${Math.round(ms / 60000)} min`;
  return `${Math.round(ms / 3600000)} h`;
}

function spanLabel(ms: number): string {
  const h = ms / 3600000;
  if (h < 1) return `${Math.round(ms / 60000)} min`;
  if (h < 48) return `${Math.round(h)} h`;
  return `${Math.round(h / 24)} days`;
}

/** Value for a datetime-local input, in the browser's timezone. */
function toLocalInput(ms: number): string {
  const d = new Date(ms - new Date(ms).getTimezoneOffset() * 60000);
  return d.toISOString().slice(0, 16);
}

function points(rows: BlePing[], pick: (p: BlePing) => number | null): LivePoint[] {
  const out: LivePoint[] = [];
  rows.forEach((p) => {
    const y = pick(p);
    if (isNum(y)) out.push({ x: p.observedAt, y });
  });
  return out;
}

type Mode = 'live' | 'history';

type RangeChipsProps = {
  mode: Mode;
  windowMin: number;
  onWindow: (minutes: number) => void;
  preset: string;
  onPreset: (key: string) => void;
  /** Extra text on the right, e.g. The exact range being shown. */
  note?: string;
};

/**
 * The range selector, slim enough to repeat above every group of graphs.
 *
 * Repeated rather than placed once at the top, because this page is several
 * screens tall: changing the range while looking at the heat map or the GPS
 * track should not mean scrolling back up and losing your place. Every copy
 * drives the SAME state, so no two graphs can end up showing different ranges
 * — which would be worse than the scrolling.
 */
function RangeChips({
  mode,
  windowMin,
  onWindow,
  preset,
  onPreset,
  note
}: RangeChipsProps): ReactElement {
  const chipClass = (active: boolean): string =>
    `rounded border px-2 py-0.5 text-xs transition ${
      active
        ? 'border-primary-500/60 bg-primary-500/20 text-primary-400'
        : 'border-gray-800 bg-background-700 text-gray-400 hover:text-gray-200'
    }`;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="mr-1 text-xs uppercase tracking-wide text-gray-500">Range</span>
      {mode === 'live'
        ? WINDOWS.map((w) => (
            <button
              key={w.key}
              type="button"
              onClick={() => onWindow(w.key)}
              className={chipClass(windowMin === w.key)}
            >
              {w.label}
            </button>
          ))
        : HISTORY_PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => onPreset(p.key)}
              className={chipClass(preset === p.key)}
            >
              {p.label}
            </button>
          ))}
      {mode === 'history' && preset === 'custom' && (
        <span className={chipClass(true)}>custom</span>
      )}
      {note && <span className="ml-1 text-xs text-gray-600">{note}</span>}
    </div>
  );
}

/**
 * Admin-only real-time graphs for ONE BLE device: every numeric parameter the
 * sensor sends (live line + window distribution), then the relaying phone's
 * GPS track, accelerometer and altitude.
 *
 * Data is pushed by the server over SSE, never polled. The stream is opened
 * for the selected device only and filtered server-side, so another device's
 * pings never reach this page; `merge` re-checks the serial as a second guard.
 * History comes from one REST call on open and after any stream gap.
 */
export default function AdminBleLivePage(): ReactElement {
  const isAllowed = useMemo(() => canUseBleAdmin(getUserNameFromSessionStorage()), []);
  // The signed-in user's threshold configuration and unit choices, so a BLE
  // reading is judged by the same bands as the same parameter anywhere else in
  // the dashboard. NOTE: these pages are cross-tenant, and this is the ADMIN's
  // configuration, not the device owner's — the legend names the config in
  // force so the colours are never ambiguous.
  const { clientThresholdSettings, userConfigDataV2 } = useData();

  const [deviceList, setDeviceList] = useState<BleDevice[] | null>(null);
  const [serial, setSerial] = useState<number | null>(null);
  const [windowMin, setWindowMin] = useState(15);
  const [paused, setPaused] = useState(false);
  /**
   * Parameters switched off, restored from localStorage on first render.
   *
   * Stored as the HIDDEN set, not the visible one: a sensor that starts
   * reporting a new field should show it, which an allow-list would suppress
   * for ever.
   */
  const [hidden, setHidden] = useState<Set<string>>(() => {
    const stored = getAppliedFilter(Filters.bleHiddenParameters)?.data;
    return new Set(Array.isArray(stored) ? stored.filter((k) => typeof k === 'string') : []);
  });
  const [status, setStatus] = useState<StreamStatus>('connecting');
  const [version, setVersion] = useState(0);
  /** Parameter colouring the GPS track; '' means a flat line. */
  const [mapColorBy, setMapColorBy] = useState<string>(
    () => getAppliedFilter(Filters.bleMapColorBy)?.data ?? ''
  );
  /**
   * 'merged' draws every parameter in one chart with its own y axis, as the
   * home screen's time-series widget does; 'separate' keeps one card per
   * parameter, which is the only place the per-parameter box plot and
   * min/mean/max row fit.
   */
  const [layout, setLayout] = useState<'merged' | 'separate'>(() =>
    getAppliedFilter(Filters.bleChartLayout)?.data === 'separate' ? 'separate' : 'merged'
  );
  const [eachWindowMin, setEachWindowMin] = useState<number>(() => {
    const stored = Number(getAppliedFilter(Filters.bleEachWindow)?.data);
    return EACH_WINDOWS.includes(stored) ? stored : EACH_WINDOWS[0];
  });
  const [roomVolume, setRoomVolume] = useState<number | null>(() => {
    const stored = Number(getAppliedFilter(Filters.bleRoomVolume)?.data);
    return Number.isFinite(stored) && stored > 0 ? stored : null;
  });
  const [channels, setChannels] = useState<BleChannelsResponse | null>(null);
  const [channelsLoading, setChannelsLoading] = useState(false);
  const [exporting, setExporting] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  // History ("go back in time"): a fixed past range, loaded once per change.
  const [mode, setMode] = useState<Mode>('live');
  const [preset, setPreset] = useState<string>('1h');
  const [range, setRange] = useState<{ from: number; to: number } | null>(null);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [history, setHistory] = useState<HistoryData | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  // Rows live in a ref, not state: they change at stream rate, and the page
  // only re-renders on the throttled `version` tick.
  const rows = useRef<BlePing[]>([]);
  const seen = useRef<Set<number>>(new Set());
  const renderTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  const serialRef = useRef(serial);
  serialRef.current = serial;

  const loadDevices = useCallback(async () => {
    try {
      const res = await axiosInstance.get<BleDevice[]>(`${BASE}/devices`);
      const list = Array.isArray(res.data) ? res.data : [];
      setDeviceList(list);
      // Default to the most recently active device.
      setSerial((current) => current ?? (list[0] ? list[0].deviceSerial : null));
    } catch {
      setDeviceList([]);
    }
  }, []);

  useEffect(() => {
    if (isAllowed) loadDevices();
  }, [isAllowed, loadDevices]);

  const requestRender = useCallback(() => {
    if (renderTimer.current || pausedRef.current) return;
    renderTimer.current = setTimeout(() => {
      renderTimer.current = null;
      setVersion((v) => v + 1);
    }, RENDER_INTERVAL_MS);
  }, []);

  const merge = useCallback(
    (incoming: BlePing[]) => {
      let added = false;
      let outOfOrder = false;
      const list = rows.current;
      incoming.forEach((p) => {
        // Second guard behind the server-side filter: only the selected device.
        if (!p || p.deviceSerial !== serialRef.current) return;
        if (seen.current.has(p.id) || !isNum(p.observedAt)) return;
        seen.current.add(p.id);
        // Phones upload in batches, so a ping can arrive after newer ones.
        if (list.length > 0 && p.observedAt < list[list.length - 1].observedAt) outOfOrder = true;
        list.push(p);
        added = true;
      });
      if (!added) return;
      if (outOfOrder) list.sort((a, b) => a.observedAt - b.observedAt);
      // Keep only the largest window, and never more than MAX_ROWS.
      const horizon = Date.now() - WINDOWS[WINDOWS.length - 1].key * 60000;
      let drop = 0;
      while (drop < list.length && list[drop].observedAt < horizon) drop += 1;
      drop = Math.max(drop, list.length - MAX_ROWS);
      if (drop > 0) list.splice(0, drop).forEach((p) => seen.current.delete(p.id));
      requestRender();
    },
    [requestRender]
  );

  /**
   * Backfills the live window from the stored pings, newest page first.
   *
   * Pages until the rows reach back past the LARGEST window this page offers,
   * not the currently-selected one, so switching from 5 min to 1 hour does not
   * need another round of fetching. Stops early on a short page (no more data)
   * or at MAX_HISTORY_PAGES.
   */
  const loadHistory = useCallback(async () => {
    if (serial === null) return;
    const horizon = Date.now() - WINDOWS[WINDOWS.length - 1].key * 60000;
    try {
      for (let page = 0; page < MAX_HISTORY_PAGES; page += 1) {
        // Sequential by nature: each page's result decides whether another is
        // needed, so these cannot be issued in parallel.
        // eslint-disable-next-line no-await-in-loop
        const res = await axiosInstance.get<BlePing[]>(`${BASE}/pings`, {
          params: { limit: HISTORY_LIMIT, offset: page * HISTORY_LIMIT, serial }
        });
        const incoming = Array.isArray(res.data) ? res.data : [];
        if (incoming.length === 0) break;
        merge(incoming);
        // The listing is newest-first, so a page's last row is its oldest.
        const oldest = incoming[incoming.length - 1]?.observedAt;
        if (incoming.length < HISTORY_LIMIT || (isNum(oldest) && oldest <= horizon)) break;
      }
    } catch {
      // The stream still works without history; the status pill shows trouble.
    }
  }, [serial, merge]);

  // One stream per selected device. Switching device starts from a clean slate.
  useEffect(() => {
    // History mode holds no stream open — browsing the past costs the server nothing.
    if (!isAllowed || serial === null || mode !== 'live') return undefined;
    rows.current = [];
    seen.current = new Set();
    setVersion((v) => v + 1);
    loadHistory();
    return openBleStream<BlePing>(String(serial), {
      onPings: merge,
      onStatus: setStatus,
      onGap: loadHistory
    });
  }, [isAllowed, serial, mode, merge, loadHistory]);

  // Scroll the time axis once a second even when nothing arrives. Local
  // redraw only — this never touches the network.
  useEffect(() => {
    if (paused || mode !== 'live') return undefined;
    const timer = setInterval(() => setVersion((v) => v + 1), 1000);
    return () => clearInterval(timer);
  }, [paused, mode]);

  const applyPreset = useCallback((key: string) => {
    const p = HISTORY_PRESETS.find((x) => x.key === key);
    if (!p) return;
    const to = Date.now();
    setPreset(key);
    setRange({ from: to - p.ms, to });
    setCustomFrom(toLocalInput(to - p.ms));
    setCustomTo(toLocalInput(to));
  }, []);

  const goHistory = useCallback(() => {
    setMode('history');
    if (!range) applyPreset(preset);
  }, [range, preset, applyPreset]);

  const applyCustom = useCallback(() => {
    const from = new Date(customFrom).getTime();
    const to = new Date(customTo).getTime();
    if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) {
      setHistoryError('Pick a start time before the end time.');
      return;
    }
    if (to - from > MAX_HISTORY_MS) {
      setHistoryError('A range can be at most 31 days.');
      return;
    }
    setPreset('custom');
    setRange({ from, to });
  }, [customFrom, customTo]);

  // One request per (device, range). The server does the downsampling, so the
  // payload stays around a thousand points however long the range is.
  useEffect(() => {
    if (!isAllowed || mode !== 'history' || serial === null || !range) return undefined;
    let cancelled = false;
    setHistoryLoading(true);
    setHistoryError(null);
    axiosInstance
      .get<HistoryData>(`${BASE}/history`, { params: { serial, from: range.from, to: range.to } })
      .then((res) => {
        if (!cancelled) setHistory(res.data);
      })
      .catch((e: any) => {
        if (cancelled) return;
        setHistory(null);
        setHistoryError(
          e?.response?.status === 400 ? 'That range is not valid.' : 'Could not load history.'
        );
      })
      .finally(() => {
        if (!cancelled) setHistoryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAllowed, mode, serial, range]);

  /**
   * The range the channel heat map and the export act on.
   *
   * In history mode that is the chosen range. In live mode it is the visible
   * window — so what you download and what you are looking at are the same
   * thing, rather than the export quietly meaning something else.
   */
  const actionRange = useMemo(() => {
    if (mode === 'history' && range) return range;
    const now = Date.now();
    return { from: now - windowMin * 60000, to: now };
    // `version` keeps the live range moving with the clock without a second timer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, range, windowMin, version]);

  // Channel heat map. Served by its own endpoint because /admin/history only
  // aggregates numeric JSON values and channel_data is an array.
  //
  // In LIVE mode this deliberately keys off `windowMin` and not `version`: the
  // stream ticks several times a second and this is a grouped SQL aggregate
  // over the whole window, so refetching on every tick would hammer the server
  // for a picture that barely changes. It refreshes on the slow timer below.
  const [channelTick, setChannelTick] = useState(0);
  useEffect(() => {
    if (mode !== 'live') return undefined;
    const timer = setInterval(() => setChannelTick((v) => v + 1), 30000);
    return () => clearInterval(timer);
  }, [mode]);

  useEffect(() => {
    if (!isAllowed || serial === null) return undefined;
    const { from, to } = mode === 'history' && range
      ? range
      : { from: Date.now() - windowMin * 60000, to: Date.now() };
    let cancelled = false;
    setChannelsLoading(true);
    getBleChannels(serial, from, to)
      .then((data) => {
        if (!cancelled) setChannels(data);
      })
      .catch(() => {
        if (!cancelled) setChannels(null);
      })
      .finally(() => {
        if (!cancelled) setChannelsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAllowed, serial, mode, range, windowMin, channelTick]);

  /** Switches a parameter on or off, remembering it for the next session. */
  const toggleParam = useCallback((key: string) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      setAppliedFilter({ key: Filters.bleHiddenParameters, data: Array.from(next).sort() });
      return next;
    });
  }, []);

  /** Picks the parameter that colours the map track, and remembers it. */
  const chooseMapColor = useCallback((key: string) => {
    setMapColorBy(key);
    setAppliedFilter({ key: Filters.bleMapColorBy, data: key });
  }, []);

  /** Switches between one merged chart and one card per parameter. */
  const chooseLayout = useCallback((next: 'merged' | 'separate') => {
    setLayout(next);
    setAppliedFilter({ key: Filters.bleChartLayout, data: next });
  }, []);

  /** Sets the eACH fitting window, and remembers it. */
  const chooseEachWindow = useCallback((minutes: number) => {
    setEachWindowMin(minutes);
    setAppliedFilter({ key: Filters.bleEachWindow, data: minutes });
  }, []);

  /** Sets the room volume used for absolute emission rates, and remembers it. */
  const chooseRoomVolume = useCallback((volume: number | null) => {
    const clean = volume !== null && Number.isFinite(volume) && volume > 0 ? volume : null;
    setRoomVolume(clean);
    setAppliedFilter({ key: Filters.bleRoomVolume, data: clean });
  }, []);

  /**
   * Adopts a range a user dragged out on any graph.
   *
   * Only in history mode: in live mode the time axis is re-pinned to
   * [now - window, now] on every tick, so a zoom would be undone within the
   * second. The chart hands the range over and drops its own zoom state, which
   * leaves this page the only owner of what every graph shows.
   */
  const handleChartRange = useCallback(
    (from: number, to: number) => {
      if (mode !== 'history') return;
      setPreset('custom');
      setRange({ from, to });
      setCustomFrom(toLocalInput(from));
      setCustomTo(toLocalInput(to));
    },
    [mode]
  );

  /** Downloads the raw pings for the acting range as CSV. */
  const handleExport = useCallback(async () => {
    if (serial === null) return;
    setExportError(null);
    setExporting('Starting…');
    try {
      await downloadBlePingCsv(serial, actionRange.from, actionRange.to, (bytes) =>
        setExporting(formatBytes(bytes))
      );
    } catch (e: any) {
      setExportError(e?.message ?? 'The export failed.');
    } finally {
      setExporting(null);
    }
  }, [serial, actionRange]);

  useEffect(
    () => () => {
      if (renderTimer.current) clearTimeout(renderTimer.current);
    },
    []
  );

  const liveView = useMemo(() => {
    const now = Date.now();
    const windowMs = windowMin * 60000;
    const inWindow = rows.current.filter((p) => p.observedAt >= now - windowMs);

    const keys = new Set<string>();
    inWindow.forEach((p) => {
      if (p.values) Object.keys(p.values).forEach((k) => isNum(p.values?.[k]) && keys.add(k));
    });
    const parameters = Array.from(keys)
      .sort()
      .map((key, i) => {
        const color = PALETTE[i % PALETTE.length];
        const pts = points(inWindow, (p) => {
          const v = p.values?.[key];
          return isNum(v) ? v : null;
        });
        const latest = pts.length > 0 ? pts[pts.length - 1].y : null;
        return {
          key,
          color,
          unit: bleUnitOf(key),
          series: [
            { label: bleLabelOf(key), color, points: pts, unit: bleUnitOf(key) }
          ] as LiveSeries[],
          stats: computeStats(
            pts.map((pt) => pt.y),
            latest
          )
        };
      });

    const magnitude = (p: BlePing): number | null =>
      isNum(p.accelX) && isNum(p.accelY) && isNum(p.accelZ)
        ? Math.sqrt(p.accelX ** 2 + p.accelY ** 2 + p.accelZ ** 2)
        : null;
    const accel: LiveSeries[] = [
      { label: 'X', color: '#F04438', points: points(inWindow, (p) => p.accelX) },
      { label: 'Y', color: '#12B76A', points: points(inWindow, (p) => p.accelY) },
      { label: 'Z', color: '#2E90FA', points: points(inWindow, (p) => p.accelZ) },
      { label: '|a|', color: '#D0D5DD', points: points(inWindow, magnitude) }
    ];
    const altitude: LiveSeries[] = [
      { label: 'Altitude', color: '#7A5AF8', points: points(inWindow, (p) => p.altitude) }
    ];
    const tracks: Track[] =
      serial === null
        ? []
        : [
            {
              key: String(serial),
              label: String(serial),
              color: TRACK_COLOR,
              points: inWindow
                .filter((p) => isNum(p.latitude) && isNum(p.longitude))
                .map((p) => {
                  const picked = mapColorBy ? p.values?.[mapColorBy] : null;
                  return {
                    lat: p.latitude as number,
                    lng: p.longitude as number,
                    t: p.observedAt,
                    v: isNum(picked) ? picked : null
                  };
                })
            }
          ];

    const last = inWindow[inWindow.length - 1];
    const perMinute = inWindow.filter((p) => p.observedAt >= now - 60000).length;
    return {
      windowMs,
      range: undefined as { from: number; to: number } | undefined,
      gapMs: 60000,
      dataExtent: null as { from: number; to: number } | null,
      tiles: [
        { label: 'Pings in window', value: inWindow.length.toLocaleString() },
        { label: 'Last minute', value: `${perMinute} pings` },
        { label: 'Latest', value: last ? ageLabel(now - last.observedAt) : '—' }
      ],
      parameters,
      accel,
      altitude,
      tracks
    };
    // `version` is the change signal for the ref-held rows.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version, windowMin, serial, mapColorBy]);

  // Same shape as the live view, built from the server's downsampled range.
  const historyView = useMemo(() => {
    if (!history) return null;
    const half = history.bucketMs / 2;
    // Plot each average at the middle of its bucket.
    const series = (pick: (p: HistoryPoint) => number | null): LivePoint[] => {
      const out: LivePoint[] = [];
      history.points.forEach((p) => {
        const y = pick(p);
        if (isNum(y)) out.push({ x: p.t + half, y });
      });
      return out;
    };
    const parameters = Object.keys(history.stats)
      .sort()
      .map((key, i) => {
        const color = PALETTE[i % PALETTE.length];
        const pts = series((p) => (isNum(p.values?.[key]) ? p.values[key] : null));
        const s = history.stats[key];
        return {
          key,
          color,
          unit: bleUnitOf(key),
          series: [
            { label: bleLabelOf(key), color, points: pts, unit: bleUnitOf(key) }
          ] as LiveSeries[],
          // The diamond marks the last reading of the range.
          stats: { ...s, latest: pts.length > 0 ? pts[pts.length - 1].y : s.p50 } as ParameterStats
        };
      });
    const first = history.points[0];
    const last = history.points[history.points.length - 1];
    return {
      windowMs: history.to - history.from,
      range: { from: history.from, to: history.to },
      // Join neighbouring buckets; break only across 3+ empty buckets.
      gapMs: Math.max(60000, history.bucketMs * 3),
      dataExtent: first && last ? { from: first.t, to: last.t + history.bucketMs } : null,
      tiles: [
        { label: 'Pings in range', value: history.rawCount.toLocaleString() },
        { label: 'Span', value: spanLabel(history.to - history.from) },
        { label: 'Each point', value: `avg of ${bucketLabel(history.bucketMs)}` }
      ],
      parameters,
      accel: [
        { label: 'X', color: '#F04438', points: series((p) => p.accelX) },
        { label: 'Y', color: '#12B76A', points: series((p) => p.accelY) },
        { label: 'Z', color: '#2E90FA', points: series((p) => p.accelZ) },
        { label: '|a| peak', color: '#D0D5DD', points: series((p) => p.accelPeak) }
      ] as LiveSeries[],
      altitude: [
        { label: 'Altitude', color: '#7A5AF8', points: series((p) => p.altitude) }
      ] as LiveSeries[],
      tracks: [
        {
          key: String(history.deviceSerial),
          label: String(history.deviceSerial),
          color: TRACK_COLOR,
          points: history.points
            .filter((p) => isNum(p.latitude) && isNum(p.longitude))
            .map((p) => {
              const picked = mapColorBy ? p.values?.[mapColorBy] : null;
              return {
                lat: p.latitude as number,
                lng: p.longitude as number,
                t: p.t,
                v: isNum(picked) ? picked : null
              };
            })
        }
      ] as Track[]
    };
  }, [history, mapColorBy]);

  const view =
    mode === 'history'
      ? historyView ?? {
          ...liveView,
          range: range ?? undefined,
          tiles: [],
          parameters: [],
          accel: [],
          altitude: [],
          tracks: []
        }
      : liveView;

  /**
   * How the GPS track is coloured.
   *
   * PREFERRED: the client's configured thresholds for that parameter, so a
   * stretch of track is green, amber or red for exactly the same reason a
   * parameter card is — the point of walking a route with a sensor is to see
   * WHERE the air was unacceptable, and only a threshold can say that.
   *
   * FALLBACK: a relative colour ramp over the 5th-95th percentile of what is
   * on screen. Used when the parameter has no dashboard equivalent (a raw
   * count, a VOC index) or its threshold is unset. Not min-max, because a
   * single spike would push every other point into the bottom colour and
   * flatten the track; the ends are clamped rather than dropped.
   *
   * An `incomplete` threshold deliberately does NOT get used: those ship with
   * every bound at 0, which `getPriorityColor` reads as "good", so the whole
   * track would paint green and claim the air was fine on no evidence.
   *
   * Declared after `view` because it reads whichever track set is on screen,
   * live or history.
   */
  const mapColoring = useMemo((): TrackColoring => {
    if (!mapColorBy) return null;
    const label = bleLabelOf(mapColorBy);
    const unit = bleUnitOf(mapColorBy);
    const parameter = bleParameterOf(mapColorBy);

    if (parameter) {
      const config = getActiveConfig(clientThresholdSettings, DeviceType.MIXED);
      const configured = config.thresholds[parameter];
      if (configured && !configured.incomplete) {
        const bounds = getThresholds(
          parameter,
          clientThresholdSettings,
          userConfigDataV2,
          DeviceType.MIXED
        );
        const num = (v: number): string =>
          Math.abs(v) >= 100 ? v.toFixed(0) : v.toFixed(Math.abs(v) >= 10 ? 1 : 2);
        const bands: ThresholdBand[] = [
          {
            label: 'Good',
            color: COLOR_BY_PRIORITY[PriorityList.low],
            detail: `${num(bounds.lowerWarning)}–${num(bounds.upperWarning)}`
          },
          {
            label: 'Warning',
            color: COLOR_BY_PRIORITY[PriorityList.medium],
            detail: `to ${num(bounds.upperCritical)}`
          },
          {
            label: 'Critical',
            color: COLOR_BY_PRIORITY[PriorityList.high],
            detail: `over ${num(bounds.upperCritical)}`
          }
        ];
        return {
          mode: 'threshold',
          key: mapColorBy,
          label,
          unit,
          configName: config.name,
          bands,
          colorOf: (value: number) =>
            COLOR_BY_PRIORITY[
              getPriorityColor(
                parameter,
                value,
                clientThresholdSettings,
                userConfigDataV2,
                DeviceType.MIXED
              )
            ] ?? COLOR_BY_PRIORITY[PriorityList.none]
        };
      }
    }

    const values: number[] = [];
    view.tracks.forEach((t) =>
      t.points.forEach((p) => {
        if (isNum(p.v)) values.push(p.v);
      })
    );
    if (values.length === 0) return null;
    const sorted = values.slice().sort((a, b) => a - b);
    const at = (q: number): number =>
      sorted[Math.min(sorted.length - 1, Math.floor(q * sorted.length))];
    const lo = at(0.05);
    const hi = at(0.95);
    // A completely flat parameter has no scale to draw; nudge so lo < hi.
    return { mode: 'ramp', key: mapColorBy, label, unit, lo, hi: hi > lo ? hi : lo + 1 };
  }, [mapColorBy, view.tracks, clientThresholdSettings, userConfigDataV2]);

  /**
   * Recompute cadence for the eACH fit in live mode. Deliberately its own
   * slow timer rather than the stream's `version`: the fit is a search over a
   * few hundred binned points per parameter, and the answer moves far slower
   * than the graphs do.
   */
  const [eachTick, setEachTick] = useState(0);
  const lastEachAt = useRef(0);
  useEffect(() => {
    if (mode !== 'live' || paused) return;
    const now = Date.now();
    if (now - lastEachAt.current < EACH_INTERVAL_MS) return;
    lastEachAt.current = now;
    setEachTick((v) => v + 1);
    // Driven by `version` — which the stream bumps on new pings and the clock
    // bumps once a second — and then throttled, rather than run on a bare
    // interval. A bare interval sits out of phase with the backfill: the first
    // tick can land before the pings do, and the card then says "no readings"
    // for a whole interval after every page load, which reads as a fault
    // rather than as buffering.
  }, [version, mode, paused]);

  /**
   * The window the eACH fit runs over, and the readings inside it.
   *
   * LIVE: a rolling window of the chosen length, taken from the raw pings —
   * that is the "continuous" calculation, redone every few seconds.
   *
   * HISTORY: the selected range, from the server's bucket averages. The chosen
   * range IS the window in that mode, so a second window control there would
   * only be able to disagree with the one on screen.
   */
  const eachInput = useMemo(() => {
    const samples: KeyedSamples = {};
    const positions: { latitude: number | null; longitude: number | null }[] = [];
    let from: number;
    let to: number;
    let spanMin: number;

    /** Files one reading under its value key. */
    const add = (key: string, t: number, y: number): void => {
      if (!samples[key]) samples[key] = [];
      samples[key].push({ t, y });
    };

    if (mode === 'history' && history) {
      from = history.from;
      to = history.to;
      spanMin = Math.round((to - from) / 60000);
      history.points.forEach((p) => {
        positions.push({ latitude: p.latitude, longitude: p.longitude });
        Object.entries(p.values ?? {}).forEach(([key, value]) => {
          if (isNum(value)) add(key, p.t, value);
        });
      });
    } else {
      to = Date.now();
      spanMin = eachWindowMin;
      from = to - spanMin * 60000;
      rows.current.forEach((p) => {
        if (p.observedAt < from) return;
        positions.push({ latitude: p.latitude, longitude: p.longitude });
        Object.entries(p.values ?? {}).forEach(([key, value]) => {
          if (isNum(value)) add(key, p.observedAt, value);
        });
      });
    }
    return { samples, positions, from, to, spanMin };
    // `eachTick` is the live recompute signal for the ref-held rows.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eachTick, mode, history, eachWindowMin, serial]);

  const eachRows = useMemo(
    (): EachRow[] =>
      EACH_TARGETS.map((target) =>
        computeEachRow(target, eachInput.samples, eachInput.from, eachInput.to, {
          volumeM3: roomVolume
        })
      ),
    [eachInput, roomVolume]
  );

  const eachRoam = useMemo(() => roamRadiusMetres(eachInput.positions), [eachInput]);

  if (!isAllowed) {
    return <Navigate to="/" replace />;
  }

  const pill = statusPill(status);
  const selected = deviceList?.find((d) => d.deviceSerial === serial);
  const shownParameters = view.parameters.filter((p) => !hidden.has(p.key));
  /**
   * Every visible parameter as datasets of ONE chart.
   *
   * Colours are the same palette the separate cards use, so switching layout
   * re-arranges the page without re-colouring anything. Each series carries
   * its own unit, because the merged chart puts CO₂ in ppm beside PM2.5 in
   * µg/m³ and a single chart-wide unit would mislabel one of them.
   */
  const mergedSeries: LiveSeries[] = shownParameters.flatMap((p) => p.series);
  /**
   * Drag-to-zoom is only wired up in history mode. In live mode the time axis
   * is re-pinned to [now - window, now] every second, so a zoom would be
   * undone before the user let go of the mouse.
   */
  const zoomHandler = mode === 'history' ? handleChartRange : undefined;
  /**
   * Parameters offered for colouring the map.
   *
   * The saved choice is listed even when the current window carries no reading
   * for it yet — otherwise the picker renders blank and the preference looks
   * like it was forgotten.
   */
  const mapColorOptions = useMemo(() => {
    const keys = view.parameters.map((p) => p.key);
    if (mapColorBy && !keys.includes(mapColorBy)) keys.unshift(mapColorBy);
    return keys.map((key) => ({ key, label: bleLabelOf(key) }));
  }, [view.parameters, mapColorBy]);

  /** One range selector, repeated above each group of graphs. */
  const rangeChips = (
    <RangeChips
      mode={mode}
      windowMin={windowMin}
      onWindow={setWindowMin}
      preset={preset}
      onPreset={applyPreset}
      note={mode === 'history' ? 'drag across a graph to zoom; shift-drag to pan' : undefined}
    />
  );

  return (
    <div className="flex flex-1 flex-col gap-4 px-6 py-6 text-gray-100">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-semibold">BLE Live Graphs</h1>
            {serial !== null && mode === 'history' && (
              <span className="rounded-full border border-primary-500/50 bg-primary-500/15 px-2.5 py-0.5 text-xs font-medium text-primary-400">
                History
              </span>
            )}
            {serial !== null && mode === 'live' && (
              <span
                className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${pill.className}`}
              >
                {status === 'live' && (
                  <span className="mr-1.5 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-current align-middle" />
                )}
                {pill.label}
              </span>
            )}
          </div>
          <p className="text-base text-gray-400">
            {selected
              ? `${selected.deviceName || 'Device'} · serial ${selected.deviceSerial}`
              : 'No device selected'}
            {' · '}
            <Link to="/admin/ble-pings" className="text-primary-400 hover:underline">
              raw table
            </Link>
          </p>
        </div>
        <div className="flex gap-3 text-base">
          {view.tiles.map((t) => (
            <div
              key={t.label}
              className="rounded border border-gray-800 bg-container-500 px-4 py-2"
            >
              <div className="text-sm text-gray-400">{t.label}</div>
              <div className="text-lg font-semibold">{t.value}</div>
            </div>
          ))}
        </div>
      </div>

      <div className={`flex flex-col gap-3 ${CARD_CLASS}`}>
        <div className="flex flex-wrap items-center gap-3">
          <label
            className="flex items-center gap-2 text-base text-gray-400"
            htmlFor="ble-live-serial"
          >
            Device
            <select
              id="ble-live-serial"
              value={serial ?? ''}
              onChange={(e) => setSerial(e.target.value ? Number(e.target.value) : null)}
              className="rounded border border-gray-800 bg-background-700 px-3 py-1.5 text-base text-gray-100 focus:border-primary-500 focus:outline-none"
            >
              {(deviceList ?? []).length === 0 && <option value="">No devices yet</option>}
              {(deviceList ?? []).map((d) => (
                <option key={d.deviceSerial} value={d.deviceSerial}>
                  {d.deviceName || 'Device'} ({d.deviceSerial}) ·{' '}
                  {ageLabel(Date.now() - d.lastSeenAt)}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={loadDevices}
            title="Reload the device list"
            className="flex items-center rounded border border-gray-800 bg-background-700 px-2 py-1.5 text-gray-300 transition hover:bg-background-500"
          >
            <span className="material-icons-outlined text-base">refresh</span>
          </button>
          <div className="flex overflow-hidden rounded border border-gray-800">
            {(['live', 'history'] as Mode[]).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => (m === 'live' ? setMode('live') : goHistory())}
                className={`flex items-center gap-1 px-3 py-1.5 text-sm transition ${
                  mode === m
                    ? 'bg-primary-500/20 text-primary-400'
                    : 'bg-background-700 text-gray-400 hover:text-gray-200'
                }`}
              >
                <span className="material-icons-outlined text-base">
                  {m === 'live' ? 'sensors' : 'history'}
                </span>
                {m === 'live' ? 'Live' : 'History'}
              </button>
            ))}
          </div>
          {/* The range selector is this one component wherever it appears, so
              the copies above each group of graphs look and behave identically
              to this one and all drive the same state. */}
          {rangeChips}
          {mode === 'live' && (
            <button
                type="button"
                onClick={() => setPaused((p) => !p)}
                className="flex items-center gap-1.5 rounded border border-gray-800 bg-background-700 px-3 py-1.5 text-sm text-gray-200 transition hover:bg-background-500"
                title="Freeze the graphs to inspect them; pings keep buffering"
              >
                <span className="material-icons-outlined text-base">
                  {paused ? 'play_arrow' : 'pause'}
                </span>
                {paused ? 'Resume' : 'Pause'}
              </button>
          )}
          {mode === 'history' && (
            <div className="flex flex-wrap items-center gap-2 text-sm text-gray-400">
                <input
                  type="datetime-local"
                  aria-label="From"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className="rounded border border-gray-800 bg-background-700 px-2 py-1.5 text-gray-100 [color-scheme:dark] focus:border-primary-500 focus:outline-none"
                />
                <span>to</span>
                <input
                  type="datetime-local"
                  aria-label="To"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className="rounded border border-gray-800 bg-background-700 px-2 py-1.5 text-gray-100 [color-scheme:dark] focus:border-primary-500 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={applyCustom}
                  className={`rounded border px-3 py-1.5 transition ${
                    preset === 'custom'
                      ? 'border-primary-500/60 bg-primary-500/20 text-primary-400'
                      : 'border-gray-800 bg-background-700 text-gray-200 hover:bg-background-500'
                  }`}
                >
                  Show range
                </button>
                {view.dataExtent &&
                  view.range &&
                  view.dataExtent.to - view.dataExtent.from <
                    (view.range.to - view.range.from) / 2 && (
                    <button
                      type="button"
                      onClick={() => {
                        const ext = view.dataExtent as { from: number; to: number };
                        // A little margin either side, and at least a minute.
                        const pad = Math.max(30000, (ext.to - ext.from) * 0.05);
                        setCustomFrom(toLocalInput(ext.from - pad));
                        setCustomTo(toLocalInput(ext.to + pad));
                        setPreset('custom');
                        setRange({ from: ext.from - pad, to: ext.to + pad });
                      }}
                      title="This device only has data in part of the range — narrow to it for full detail"
                      className="flex items-center gap-1 rounded border border-success-500/40 bg-success-500/15 px-3 py-1.5 text-success-400 transition hover:bg-success-500/25"
                    >
                      <span className="material-icons-outlined text-base">zoom_in</span>
                      Zoom to data
                    </button>
                  )}
                {historyLoading && (
                  <span className="material-icons-outlined animate-spin text-base text-gray-400">
                    autorenew
                  </span>
                )}
              </div>
          )}
        </div>
        {/* Export + map colouring. Both act on the range shown in the label,
            so there is no doubt about what a download contains. */}
        <div className="flex flex-wrap items-center gap-3 border-t border-gray-800 pt-3">
          <button
            type="button"
            onClick={() => {
              handleExport();
            }}
            disabled={serial === null || exporting !== null}
            className="flex items-center gap-1.5 rounded border border-gray-800 bg-background-700 px-3 py-1.5 text-sm text-gray-200 transition hover:bg-background-500 disabled:opacity-50"
            title="Download every raw ping in this range as CSV"
          >
            <span className="material-icons-outlined text-base">download</span>
            {exporting === null ? 'Download CSV' : `Downloading… ${exporting}`}
          </button>
          <span className="text-xs text-gray-500">
            {new Date(actionRange.from).toLocaleString()} →{' '}
            {new Date(actionRange.to).toLocaleString()}
          </span>
        </div>
        {exportError && (
          <div className="rounded border border-error-500/40 bg-error-500/10 px-3 py-2 text-sm text-error-400">
            {exportError}
          </div>
        )}

        {view.parameters.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="mr-1 text-sm text-gray-400">Parameters</span>
            {view.parameters.map((p) => {
              const on = !hidden.has(p.key);
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => toggleParam(p.key)}
                  className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition ${
                    on
                      ? 'border-gray-600 bg-background-700 text-gray-100'
                      : 'border-gray-800 text-gray-500 hover:text-gray-300'
                  }`}
                >
                  <span
                    className="inline-block h-2 w-2 rounded-full"
                    style={{ backgroundColor: on ? p.color : '#475467' }}
                  />
                  {p.key.replace(/_/g, ' ')}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {mode === 'history' && historyError && (
        <div className="rounded border border-error-500/40 bg-error-500/10 px-4 py-3 text-error-400">
          {historyError}
        </div>
      )}
      {mode === 'history' && history && history.rawCount === 0 && !historyLoading && (
        <div className={`${CARD_CLASS} text-gray-400`}>
          This device sent no pings between {new Date(history.from).toLocaleString()} and{' '}
          {new Date(history.to).toLocaleString()}.
        </div>
      )}
      {mode === 'live' && status === 'denied' && (
        <div className="rounded border border-error-500/40 bg-error-500/10 px-4 py-3 text-error-400">
          Live stream unavailable. Your session could not be refreshed — reload the page, and
          sign in again if it keeps happening. (The rest of this page still works; only the
          live push is affected.)
        </div>
      )}
      {deviceList !== null && deviceList.length === 0 && (
        <div className={`${CARD_CLASS} text-gray-400`}>
          No device has sent BLE pings yet. Start the app (or the simulator) and press refresh.
        </div>
      )}

      {serial !== null && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-lg font-semibold text-gray-200">Sensor parameters</h2>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex overflow-hidden rounded border border-gray-800">
                {(['merged', 'separate'] as const).map((l) => (
                  <button
                    key={l}
                    type="button"
                    onClick={() => chooseLayout(l)}
                    title={
                      l === 'merged'
                        ? 'All parameters in one chart, each on its own scale'
                        : 'One chart and distribution per parameter'
                    }
                    className={`flex items-center gap-1 px-2.5 py-1 text-xs transition ${
                      layout === l
                        ? 'bg-primary-500/20 text-primary-400'
                        : 'bg-background-700 text-gray-400 hover:text-gray-200'
                    }`}
                  >
                    <span className="material-icons-outlined text-sm">
                      {l === 'merged' ? 'show_chart' : 'grid_view'}
                    </span>
                    {l === 'merged' ? 'Merged' : 'Separate'}
                  </button>
                ))}
              </div>
            </div>
          </div>
          {shownParameters.length === 0 ? (
            <div className={`${CARD_CLASS} text-gray-400`}>
              {view.parameters.length === 0
                ? 'Waiting for readings from this device…'
                : 'All parameters are hidden — pick one above.'}
            </div>
          ) : null}
          {shownParameters.length > 0 && layout === 'merged' && (
            /* One chart, every parameter — the home screen's arrangement. Each
               series keeps its own y axis: CO₂ in the hundreds and PM2.5 in
               single digits cannot share a scale without flattening one of
               them onto the baseline. Only the first axis is labelled; the
               tooltip carries each series' own value and unit. */
            <div className={`flex min-w-0 flex-col gap-2 ${CARD_CLASS}`}>
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <h3 className="text-base font-semibold text-gray-100">
                  All parameters
                  <span className="ml-2 text-xs font-normal text-gray-500">
                    {shownParameters.length} shown · each on its own scale
                  </span>
                </h3>
                <div className="flex flex-wrap items-baseline gap-3 text-xs">
                  {shownParameters.map((p) => (
                    <span key={p.key} className="flex items-baseline gap-1">
                      <span style={{ color: p.color }}>{bleLabelOf(p.key)}</span>
                      <span className="font-semibold text-gray-200">
                        {p.stats ? fmt(p.stats.latest) : '—'}
                      </span>
                      <span className="text-gray-500">{p.unit}</span>
                    </span>
                  ))}
                </div>
              </div>
              <LiveLineChart
                series={mergedSeries}
                windowMs={view.windowMs}
                range={view.range}
                gapMs={view.gapMs}
                version={version}
                axisMode="perSeries"
                onRangeChange={zoomHandler}
                height="h-96"
              />
            </div>
          )}
          {shownParameters.length > 0 && layout === 'separate' && (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              {shownParameters.map((p) => (
                <div key={p.key} className={`flex min-w-0 flex-col gap-2 ${CARD_CLASS}`}>
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="text-base font-semibold text-gray-100">
                      {bleLabelOf(p.key)}
                      {p.unit && <span className="ml-1 text-xs text-gray-500">{p.unit}</span>}
                    </h3>
                    {p.stats && (
                      <div className="flex items-baseline gap-4 text-right">
                        <span className="text-xs text-gray-400">
                          mean {fmt(p.stats.mean)} · min {fmt(p.stats.min)} · max {fmt(p.stats.max)}{' '}
                          · n {p.stats.count}
                        </span>
                        <span className="text-2xl font-semibold" style={{ color: p.color }}>
                          {fmt(p.stats.latest)}
                        </span>
                      </div>
                    )}
                  </div>
                  <LiveLineChart
                    series={p.series}
                    windowMs={view.windowMs}
                    range={view.range}
                    gapMs={view.gapMs}
                    version={version}
                    onRangeChange={zoomHandler}
                  />
                  <ParameterBoxPlot stats={p.stats} color={p.color} />
                </div>
              ))}
            </div>
          )}

          <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-lg font-semibold text-gray-200">Ventilation</h2>
            {rangeChips}
          </div>
          <VentilationCard
            rows={eachRows}
            windowMin={eachInput.spanMin}
            windowChoices={mode === 'live' ? EACH_WINDOWS : []}
            onWindowChange={chooseEachWindow}
            volumeM3={roomVolume}
            onVolumeChange={chooseRoomVolume}
            roamMetres={eachRoam}
            loading={mode === 'live' && rows.current.length === 0}
          />

          <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-lg font-semibold text-gray-200">Size channels</h2>
            {rangeChips}
          </div>
          <ChannelHeatMap
            cells={channels?.rows ?? []}
            channelCount={channels?.channelCount ?? 0}
            from={channels?.from ?? actionRange.from}
            to={channels?.to ?? actionRange.to}
            bucketMs={channels?.bucketMs ?? 60000}
            min={channels?.min ?? null}
            max={channels?.max ?? null}
            parameterKey={channels?.key ?? CHANNEL_KEY}
            loading={channelsLoading}
          />

          <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-lg font-semibold text-gray-200">Phone context</h2>
            {rangeChips}
          </div>
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <LiveTrackMap
              tracks={view.tracks}
              version={version}
              colorBy={mapColoring}
              colorOptions={mapColorOptions}
              colorKey={mapColorBy}
              onColorChange={chooseMapColor}
            />
            <div className="flex min-w-0 flex-col gap-4">
              <div className={`flex min-w-0 flex-col gap-2 ${CARD_CLASS}`}>
                <div className="flex items-baseline justify-between">
                  <h3 className="text-base font-semibold text-gray-100">Accelerometer</h3>
                  <span className="text-xs text-gray-500">g · |a| ≈ 1 at rest</span>
                </div>
                <LiveLineChart
                  series={view.accel}
                  windowMs={view.windowMs}
                  range={view.range}
                  gapMs={view.gapMs}
                  version={version}
                  unit="g"
                  height="h-40"
                  onRangeChange={zoomHandler}
                />
              </div>
              <div className={`flex min-w-0 flex-col gap-2 ${CARD_CLASS}`}>
                <div className="flex items-baseline justify-between">
                  <h3 className="text-base font-semibold text-gray-100">Altitude</h3>
                  <span className="text-xs text-gray-500">m</span>
                </div>
                <LiveLineChart
                  series={view.altitude}
                  windowMs={view.windowMs}
                  range={view.range}
                  gapMs={view.gapMs}
                  version={version}
                  unit="m"
                  height="h-40"
                  onRangeChange={zoomHandler}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
